import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Alert,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
  Link,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import CategoryGrid from "./components/CategoryGrid";
import ProgressBar from "./components/Progressbar";
import ResultaatScherm from "./components/ResultaatScherm";
import ScoreManager from "./components/ScoreManager";
import SpierDialog from "./components/SpierDialog";


import "./App.css";

function App() {
  const [uuidMapping, setUuidMapping] = useState([]);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [tempUsername, setTempUsername] = useState(username);

  const nodeRef = useRef(null);

  const handleUsernameSubmit = () => {
    localStorage.setItem("username", tempUsername);
    setUsername(tempUsername);
  };
  const loadProgress = (uuid) => {
    const storedData = JSON.parse(localStorage.getItem("quizProgress")) || {};
    if (storedData[uuid]) {
      setUserAnswers(storedData[uuid].answers[currentQuestionIndex] || []);
      setUsername(storedData[uuid].username || "");
    }
  };
  
  const handleCategoryChange = (uuid) => {
    setSelectedUuid(uuid);
    setQuestions([]);
    setShowResult(false);
    setIsAnswerCorrect(false);
    setQuizFinished(false);
  
    // Haal voortgang op uit localStorage
    const storedData = JSON.parse(localStorage.getItem("quizProgress")) || {};
    const progress = storedData[uuid]?.answers || {};
    const answeredQuestions = Object.keys(progress).length;
  
    // Vragen ophalen en controleren op voltooiing
    const filePath = `./vragen/${uuid}.json`;
    fetch(filePath)
      .then((res) => res.json())
      .then((data) => {
        const totalQuestions = data.vragen.length;
  
        // Controleer of alle vragen zijn beantwoordF
        if (answeredQuestions === totalQuestions) {
          setScore(
            Object.values(progress).filter((isCorrect) => isCorrect).length
          ); // Bereken de score
          setQuestions(data.vragen); // Sla vragen op voor ResultaatScherm
          setQuizFinished(true); // Markeer de quiz als voltooid
        } else {
          setQuestions(data.vragen); // Laad de vragen als nog niet voltooid
          setCurrentQuestionIndex(
            Object.keys(progress).length > 0 ? answeredQuestions : 0
          ); // Ga naar de volgende onbeantwoorde vraag
          setUserAnswers([]); // Reset huidige antwoorden
        }
      })
      .catch((error) =>
        console.error("Fout bij het laden van de vragen:", error)
      );
  };
  
  

  const handleResetQuiz = () => {
    // Reset alle relevante states
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setScore(0);
    setShowResult(false);
    setIsAnswerCorrect(false);
  
    // Verwijder de voortgang van de huidige quiz uit localStorage
    const storedData = JSON.parse(localStorage.getItem("quizProgress")) || {};
    if (storedData[selectedUuid]) {
      storedData[selectedUuid].answers = {}; // Reset alleen de antwoorden voor deze quiz
      localStorage.setItem("quizProgress", JSON.stringify(storedData));
    }
  
    // Vragen opnieuw laden
    const filePath = `./vragen/${selectedUuid}.json`;
    fetch(filePath)
      .then((res) => res.json())
      .then((data) => {
        setQuestions(data.vragen);
      })
      .catch((error) =>
        console.error("Fout bij het opnieuw laden van de vragen:", error)
      );
  };
  

  const handleQuitQuiz = () => {
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setScore(0);
    setShowResult(false);
    setIsAnswerCorrect(false);
    setQuizFinished(false);
    setSelectedUuid(""); // Reset de geselecteerde categorie
  };
  

  const handleAnswerSelect = (answerId) => {
    const currentAnswers = userAnswers.includes(answerId)
      ? userAnswers.filter((id) => id !== answerId)
      : [...userAnswers, answerId];
    setUserAnswers(currentAnswers);
  
    // Sla het huidige antwoord op
    saveAnswerProgress(selectedUuid, currentQuestionIndex, currentAnswers);
  };

  const handleSubmit = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const correctAnswers = Object.keys(currentQuestion.respons).filter(
      (key) => currentQuestion.respons[key].status
    );
  
    const isCorrect =
      correctAnswers.every((id) => userAnswers.includes(id)) &&
      correctAnswers.length === userAnswers.length;
  
    setIsAnswerCorrect(isCorrect);
    if (isCorrect) setScore(score + 1);
  
    // Bewaar de antwoorden definitief
    saveAnswerProgress(selectedUuid, currentQuestionIndex, userAnswers);
    setShowResult(true);
  };
  

  const handleNext = () => {
    const nextIndex = currentQuestionIndex + 1;
    setUserAnswers([]);
    setShowResult(false);
    setIsAnswerCorrect(false);

    if (nextIndex < questions.length) {
      setCurrentQuestionIndex(nextIndex);
    } else {
      setQuizFinished(true);
    }
  };

    // Helper functie om spans met data-hints te vervangen door SpierDialog
    const renderWithSpierDialog = (text) => {
      return text.split(/(<span.*?data-hint="(.*?)".*?>.*?<\/span>)/g).map((part, index) => {
        const match = part.match(/data-hint="(.*?)"/);
        if (match) {
          const dataHint = match[1];
          return <SpierDialog key={index} dataHint={dataHint} />;
        }
        return <span key={index} >{part.replace(/_/g, " ")}</span>;
      });
    };
    

    const saveAnswerProgress = (uuid, questionIndex, answers) => {
      const storedData = JSON.parse(localStorage.getItem("quizProgress")) || {};
    
      if (!storedData[uuid]) storedData[uuid] = { username, answers: {} };
    
      const question = questions[questionIndex];
      const correctAnswers = Object.keys(question.respons).filter(
        (key) => question.respons[key].status
      );
    
      // Controleer of het antwoord juist is
      const isCorrect =
        correctAnswers.every((id) => answers.includes(id)) &&
        correctAnswers.length === answers.length;
    
      // Sla de antwoorden en status op
      storedData[uuid].answers[questionIndex] = { answers, isCorrect };
    
      localStorage.setItem("quizProgress", JSON.stringify(storedData));
    };
    

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <Container maxWidth="md">
     {questions.length > 0 && (
  <Box style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
    <Button
      variant="outlined"
      color="secondary"
      onClick={handleQuitQuiz}
    >
      Terug naar vragen 
    </Button>
    <Button
      variant="outlined"
      color="primary"
      onClick={handleResetQuiz}
    >
      Herstart
    </Button>
  </Box>
)}
      <Box style={{ padding: "20px" }}>
        {!username ? (
          <Box>
            <TextField
              label="Je naam"
              variant="outlined"
              value={tempUsername}
              onChange={(e) => setTempUsername(e.target.value)}
              style={{ marginBottom: "20px" }}
              fullWidth
            />
             <Button
                variant="contained"
                color="primary"
                onClick={handleUsernameSubmit}
                style={{ marginTop: "10px" }}
                disabled={!tempUsername.trim()}
              >
              Bevestig Naam
            </Button>
          </Box>
        ) : !questions.length && !quizFinished ? (
          <CategoryGrid onCategoryChange={handleCategoryChange} />

        ) : quizFinished ? (
          <ResultaatScherm
          score={score}
          uuid={selectedUuid} // Zorg ervoor dat de geselecteerde UUID wordt doorgegeven

          totalQuestions={questions.length}
          questions={questions}
          userAnswers={
            JSON.parse(localStorage.getItem("quizProgress"))?.[selectedUuid]?.answers || {}
          }
          onRestart={() => handleCategoryChange(selectedUuid)}
        />
        
        ) : (
          questions.length > 0 && (
            <>
              <ProgressBar
                currentStep={currentQuestionIndex}
                totalSteps={questions.length}
                score={score}
              />

              <TransitionGroup>
                <CSSTransition
                  key={currentQuestionIndex}
                  timeout={500}
                  classNames="slide-fade"
                  nodeRef={nodeRef}
                >
                  <Box ref={nodeRef}>
                    <hr style={{ margin: "20px 0" }} />

                    <Typography variant="h5" gutterBottom>
                    {renderWithSpierDialog(currentQuestion.vraag) }
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginTop: "10px" }}
                    >
                      {currentQuestion.type === "single"
                        ? "Selecteer één correct antwoord"
                        : "Selecteer alle correcte antwoorden"}
                    </Typography>
                    <hr style={{ margin: "20px 0" }} />

                    {showResult && (
                      <Alert
                        icon={
                          isAnswerCorrect ? (
                            <CheckCircleIcon fontSize="inherit" />
                          ) : (
                            <ErrorIcon fontSize="inherit" />
                          )
                        }
                        severity={isAnswerCorrect ? "success" : "error"}
                        style={{ marginBottom: "20px" }}
                      >
                        {isAnswerCorrect
                          ? "Je antwoord is volledig correct!"
                          : "Je antwoord is niet correct. "}
                      </Alert>
                    )}

                    {!showResult && (
                      <FormGroup>
                        {Object.keys(currentQuestion.respons).map((key) => {
                          const option = currentQuestion.respons[key];
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  checked={userAnswers.includes(key)}
                                  onChange={() => handleAnswerSelect(key)}
                                />
                              }
                              label={option.antwoord}
                            />
                          );
                        })}
                      </FormGroup>
                    )}

                    {showResult && (
                      <List>
                        {Object.keys(currentQuestion.respons).map((key) => {
                          const option = currentQuestion.respons[key];
                          const isCorrect = option.status;
                          const isSelected = userAnswers.includes(key);
                          return (
                            <ListItem key={key}>
                              <ListItemAvatar>
                                <Avatar
                                  style={{
                                    backgroundColor: isCorrect
                                      ? "green"
                                      : isSelected
                                      ? "orange"
                                      : "gray",
                                  }}
                                >
                                  {isCorrect ? <CheckCircleIcon /> : <ErrorIcon />}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={option.antwoord}
                                secondary={option.uitleg}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}

                    {showResult ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        style={{ marginTop: "10px" }}
                      >
                        {currentQuestionIndex < questions.length - 1
                          ? "Volgende Vraag"
                          : "Toon Eindresultaat"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        style={{ marginTop: "10px" }}
                      >
                        Bevestigen
                      </Button>
                    )}
                  </Box>
                </CSSTransition>
              </TransitionGroup>
            </>
          )
        )}
      </Box>
    </Container>
  );
}

export default App;
